import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router"
import { useAuthStore } from "@/stores/auth"

const PublicLayout = () => import("@/layouts/PublicLayout.vue")
const AdminLayout = () => import("@/layouts/AdminLayout.vue")

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    component: PublicLayout,
    children: [
      { path: "", name: "home", component: () => import("@/views/public/HomeView.vue") },
      { path: "pricing", name: "pricing", component: () => import("@/views/public/PricingView.vue") },
      { path: "download", name: "download", component: () => import("@/views/public/DownloadView.vue") },
      { path: "auth", name: "auth", component: () => import("@/views/public/AuthView.vue") },
      {
        path: "dashboard",
        name: "dashboard",
        component: () => import("@/views/public/DashboardView.vue"),
        meta: { requiresUser: true },
      },
      {
        path: "pay/success",
        name: "pay-success",
        component: () => import("@/views/public/PaySuccessView.vue"),
      },
    ],
  },
  {
    path: "/admin",
    children: [
      {
        path: "login",
        name: "admin-login",
        component: () => import("@/views/admin/AdminLoginView.vue"),
      },
      {
        path: "",
        component: AdminLayout,
        meta: { requiresAdmin: true },
        children: [
          { path: "", redirect: "/admin/dashboard" },
          {
            path: "dashboard",
            name: "admin-dashboard",
            component: () => import("@/views/admin/AdminDashboardView.vue"),
          },
          {
            path: "orders",
            name: "admin-orders",
            component: () => import("@/views/admin/AdminOrdersView.vue"),
          },
          {
            path: "users",
            name: "admin-users",
            component: () => import("@/views/admin/AdminUsersView.vue"),
          },
          {
            path: "products",
            name: "admin-products",
            component: () => import("@/views/admin/AdminProductsView.vue"),
          },
          {
            path: "referral-config",
            name: "admin-referral-config",
            component: () => import("@/views/admin/AdminReferralConfigView.vue"),
          },
          {
            path: "payment-config",
            name: "admin-payment-config",
            component: () => import("@/views/admin/AdminPaymentConfigView.vue"),
          },
          {
            path: "ads",
            name: "admin-ads",
            component: () => import("@/views/admin/AdminAdsView.vue"),
          },
          {
            path: "audit-log",
            name: "admin-audit-log",
            component: () => import("@/views/admin/AdminAuditLogView.vue"),
          },
          {
            path: "change-password",
            name: "admin-change-password",
            component: () => import("@/views/admin/AdminChangePasswordView.vue"),
          },
        ],
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/public/NotFoundView.vue"),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  if (!auth.isUser && to.meta.requiresUser) {
    return { name: "auth", query: { redirect: to.fullPath } }
  }
  if (!auth.isAdmin && to.meta.requiresAdmin) {
    return { name: "admin-login", query: { redirect: to.fullPath } }
  }
})

// Recover from "stale SPA bundle" after a redeploy.
// Old open tabs hold an in-memory index.js whose lazy-import URLs point to
// chunk filenames that were `rsync --delete`d off the server. Without this
// the user sees a half-rendered page (header only) and no console error
// surfaces past the dev tools — just a route navigation that silently
// resolves to nothing. Detect chunk-load failures and full-reload to the
// target path so the browser fetches the new bundle.
router.onError((err, to) => {
  const msg = String(err?.message ?? err)
  const isChunkError =
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    (err instanceof TypeError && /dynamically imported/i.test(msg))
  if (isChunkError) {
    // Avoid an infinite reload loop if the new bundle has the same issue.
    const RELOAD_FLAG = "__cfw_reloaded_on_chunk_error"
    if (!sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1")
      window.location.assign(to.fullPath || "/")
    }
  }
})

// Successful navigation = bundle is current; clear the reload flag.
router.afterEach(() => {
  try { sessionStorage.removeItem("__cfw_reloaded_on_chunk_error") } catch {}
})
